"use client";

// 2026-08-29 minimalista pro — websearch 2026 Geist + Ethora 2026-06-02 — focus trap + restore preservado, superficies single border+shadow
import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
import { useDictContext } from "@/lib/locale-context";
interface DialogContextValue {
  open: boolean;
  setOpen: (v: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  /** 2026-09-23 FASE 2 F-01 (research 2026 WAI-ARIA APG dialog + WCAG 2.2 AA
      1.3.1/4.1.2: el diálogo debe referenciar su título visible vía
      aria-labelledby, no solo role="dialog" huérfano). El DialogTitle
      registra su id aquí; DialogContent lo consume salvo aria-label(ledby)
      explícito del autor. */
  titleId: string | null;
  setTitleId: (id: string | null) => void;
}
const DialogContext = React.createContext<DialogContextValue | null>(null);
export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}
export function Dialog(t0) {
  const $ = _c(7);
  const {
    open,
    onOpenChange,
    children
  } = t0;
  const triggerRef = React.useRef(null);
  const [titleId, setTitleId] = React.useState(null);
  let t1;
  if ($[0] !== onOpenChange || $[1] !== open || $[2] !== titleId) {
    t1 = {
      open,
      setOpen: onOpenChange,
      triggerRef,
      titleId,
      setTitleId
    };
    $[0] = onOpenChange;
    $[1] = open;
    $[2] = titleId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const value = t1;
  let t2;
  if ($[4] !== children || $[5] !== value) {
    t2 = <DialogContext.Provider value={value}>{children}</DialogContext.Provider>;
    $[4] = children;
    $[5] = value;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export interface DialogTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}
export function DialogTrigger({
  asChild,
  children,
  onClick,
  ...props
}: DialogTriggerProps) {
  const ctx = React.useContext(DialogContext);
  if (!ctx) throw new Error("DialogTrigger must be inside Dialog");
  const Comp = asChild ? Slot : "button";
  return <Comp
  // eslint-disable-next-line react-hooks/refs -- triggerRef es un ref estable del contexto (nunca se lee .current en render); pasarlo como ref al trigger es el patrón documentado de Radix Slot, no una lectura durante el render
  ref={ctx.triggerRef} aria-haspopup="dialog"
  // eslint-disable-next-line react-hooks/refs
  aria-expanded={ctx.open} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement> & React.MouseEvent<HTMLButtonElement>);
    if (!e.defaultPrevented) ctx.setOpen(!ctx.open);
  }} {...props}>
      {children}
    </Comp>;
}
export interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  showCloseButton?: boolean;
  /** 2026-09-19 i18n (WCAG 2.2 AA 3.3.2 + audit i18n 14 locales): etiqueta del
      botón cerrar. Si se omite, se resuelve de `commonClose` del dict activo
      (las 14 locales lo tienen) con fallback "Close" sin provider (tests). */
  closeLabel?: string;
}
export function DialogContent({
  className,
  children,
  showCloseButton = false,
  closeLabel,
  ...props
}: DialogContentProps) {
  const ctx = React.useContext(DialogContext);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const previousActiveRef = React.useRef<HTMLElement | null>(null);
  // 2026-09-19 i18n close: dict activo ({} sin provider → fallback "Close").
  // Sin keys nuevas — `commonClose` existe en las 14 locales (audit:i18n verde).
  // 2026-09-19 hooks-first (WCAG + react-hooks/rules-of-hooks): todos los hooks
  // antes del throw — el orden es estable haya o no provider.
  const dictClose = useDictContext()?.["commonClose"];
  const {
    shouldRender,
    isLeaving
  } = useAnimatedOpen(ctx?.open ?? false, 150);
  if (!ctx) throw new Error("DialogContent must be inside Dialog");
  const {
    open,
    setOpen,
    triggerRef
  } = ctx;
  const resolvedCloseLabel = closeLabel ?? dictClose ?? "Close";
  // F-01: labelledby del título registrado (salvo override explícito del autor).
  const authorLabelledBy = (props as Record<string, unknown>)["aria-labelledby"] as string | undefined;
  const authorLabel = (props as Record<string, unknown>)["aria-label"] as string | undefined;
  const labelledBy = authorLabelledBy ?? authorLabel ? undefined : ctx.titleId ?? undefined;
  React.useEffect(() => {
    if (!open) return;
    previousActiveRef.current = document.activeElement as HTMLElement | null;
    const el = contentRef.current;
    if (!el) return;
    // focus first focusable or content itself
    const focusable = el.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
    const first = focusable[0] ?? el;
    // rAF to avoid race with portal mount
    const rafId = requestAnimationFrame(() => {
      if (!el.contains(document.activeElement)) {
        (first as HTMLElement).focus();
      }
    });
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        return;
      }
      if (e.key === "Tab") {
        const nodes = Array.from(el.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'));
        if (nodes.length === 0) {
          e.preventDefault();
          return;
        }
        const firstNode = nodes[0]!;
        const lastNode = nodes[nodes.length - 1]!;
        if (e.shiftKey && document.activeElement === firstNode) {
          e.preventDefault();
          lastNode.focus();
        } else if (!e.shiftKey && document.activeElement === lastNode) {
          e.preventDefault();
          firstNode.focus();
        }
      }
    };

    // P2 a11y: trap a nivel document en capture — el foco puede estar fuera
    // del contenido (overlay, chrome) y el listener local nunca lo vería.
    document.addEventListener("keydown", handleKeyDown, true);
    // P2 a11y: fondo inert — Tab/lectores no alcanzan el contenido detrás
    // del modal (el portal vive directo en body).
    const siblings = Array.from(document.body.children).filter(child => !child.querySelector?.("[data-dialog-overlay]") && !child.contains(el)) as HTMLElement[];
    for (const sibling of siblings) {
      if (sibling.tagName === "SCRIPT" || sibling.tagName === "STYLE") continue;
      sibling.setAttribute("inert", "");
    }
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("keydown", handleKeyDown, true);
      for (const sibling of siblings) sibling.removeAttribute("inert");
    };
    // 2026-09-12: shouldRender en deps — el portal monta un tick después de
    // open (useAnimatedOpen retrasa shouldRender), así que en el primer efecto
    // contentRef aún es null y el early-return dejaba focus/Escape/Tab sin
    // instalar para siempre (deps estables nunca re-ejecutaban).
  }, [open, setOpen, shouldRender]);
  React.useEffect(() => {
    // Capture refs at effect start to avoid stale cleanup reads
    const prevActive = previousActiveRef.current;
    const triggerElAtStart = triggerRef.current as HTMLElement | null;
    if (open) {
      const y = window.scrollY;
      // lock body scroll like Header does, but simpler: keep scroll position
      const prevOverflow = document.body.style.overflow;
      const prevPosition = document.body.style.position;
      const prevTop = document.body.style.top;
      document.body.style.overflow = "hidden";
      // compensate? keep simple overflow hidden
      return () => {
        document.body.style.overflow = prevOverflow;
        document.body.style.position = prevPosition;
        document.body.style.top = prevTop;
        // focus restore — use captured values
        const toFocus = prevActive ?? triggerElAtStart;
        // restore focus after close animation frame
        requestAnimationFrame(() => toFocus?.focus());
        // ensure scroll not jumped
        void y;
      };
    }
    // when closed, ensure previous focus is restored (if not already)
    if (!open && prevActive) {
      const toFocus = prevActive ?? triggerElAtStart;
      // delay to let portal unmount
      setTimeout(() => toFocus?.focus(), 0);
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- triggerRef stable, prevActive captured
  }, [open]);
  if (!shouldRender) return null;
  if (typeof document === "undefined") return null;
  return createPortal(<div className="vortixy-safe-area fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div data-dialog-overlay
    // 2026-09-18 FASE 3 FIX (research 2026 backdrop-filter cost + WCAG
    // vestibular: 12px de blur trasero es glass multi-capa y cuesta GPU en
    // cada frame del zoom-in; dim opaco + blur-sm 4px es suficiente para
    // separar el modal sin mareo ni repaint caro).
    // 2026-09-23 FASE 3 FIXER diseño H1 (research 2026 MDN backdrop-filter
    // 2026-04-20 + lovable.dev 2025-12-19: incluso blur-sm 4px es 2ª capa
    // GPU sobre dim opaco — dim bg-black/50 basta; panel modal-surface).
    className="fixed inset-0 bg-black/50 dark:bg-black/70" onClick={() => setOpen(false)} aria-hidden="true" />
      <div ref={contentRef} role="dialog" aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1} className={cn(
    // 2026-09-11 Lovable-hero: rounded 24px + estilo lovable-card
    // (--static anula el hover-lift: un modal no se mueve).
    // focus-trap/inert intactos — solo cambian clases visuales.
    // 2026-09-12 responsive: sin mx-4 (el p-4 del wrapper ya da gutter;
    // w-full + mx-4 dejaba gutter 0 en 360px).
    // 2026-09-16 fondo opaco: modal-surface (en dark .lovable-card es 75%).
    "lovable-card lovable-card--static modal-surface relative z-10 max-h-[90dvh] w-full max-w-[min(32rem,calc(100vw-2rem))] overflow-auto rounded-[var(--radius-hero-card,24px)] p-5 outline-none focus-visible:ring-2 focus-visible:ring-[var(--hero-focus-ring,#a855f7)] sm:p-6", isLeaving ? "animate-out fade-out zoom-out-95" : "animate-in fade-in zoom-in-95", className)} {...props}>
        {children}
        {showCloseButton ? <button type="button" aria-label={resolvedCloseLabel} onClick={() => setOpen(false)} className="absolute right-3 top-3 inline-flex size-8 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--card)] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]">
            {/* 2026-09-21 FASE 2 FIXER a11y: glifo × literal → icono Lucide X
                como el resto (chat-sidebar, Header) — el × dependía de la
                fuente y se leía inconsistente en SR/zoom. */}
            <X className="size-4" aria-hidden="true" />
          </button> : null}
      </div>
    </div>, document.body);
}
export function DialogHeader(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mb-4 space-y-1.5", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DialogTitle(t0) {
  const $ = _c(14);
  let className;
  let idProp;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      id: idProp,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = idProp;
    $[3] = props;
  } else {
    className = $[1];
    idProp = $[2];
    props = $[3];
  }
  const ctx = React.useContext(DialogContext);
  const setTitleId = ctx?.setTitleId;
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  let t1;
  let t2;
  if ($[4] !== id || $[5] !== setTitleId) {
    t1 = () => {
      if (!setTitleId) {
        return;
      }
      setTitleId(id);
      return () => setTitleId(null);
    };
    t2 = [setTitleId, id];
    $[4] = id;
    $[5] = setTitleId;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  React.useEffect(t1, t2);
  let t3;
  if ($[8] !== className) {
    t3 = cn("text-lg font-semibold leading-none tracking-tight", className);
    $[8] = className;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== id || $[11] !== props || $[12] !== t3) {
    t4 = <h2 id={id} className={t3} {...props} />;
    $[10] = id;
    $[11] = props;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export function DialogDescription(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sm text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <p className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DialogFooter(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>button]:w-full [&>button]:sm:w-auto", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DialogClose(t0) {
  const $ = _c(9);
  let asChild;
  let props;
  if ($[0] !== t0) {
    ({
      asChild,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = props;
  } else {
    asChild = $[1];
    props = $[2];
  }
  const ctx = React.useContext(DialogContext);
  if (!ctx) {
    throw new Error("DialogClose must be inside Dialog");
  }
  const Comp = asChild ? Slot : "button";
  let t1;
  if ($[3] !== ctx) {
    t1 = () => ctx.setOpen(false);
    $[3] = ctx;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== Comp || $[6] !== props || $[7] !== t1) {
    t2 = <Comp type="button" onClick={t1} {...props} />;
    $[5] = Comp;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}